<script setup>
import { watch, computed, defineEmits, defineProps, ref } from 'vue'

const emits = defineEmits(['update'])

const props = defineProps({
  data: { type: [null, String], required: true },
  editable: { type: Boolean, required: false, default: false },
  classes: { type: String, required: false },
  allowEmpty: { type: Boolean, required: false, default: true },
})

const displayValue = ref('')
const isEditing = ref(false)
const lastCommittedValue = ref('')

const DATE_PREFIX_PATTERN = /^(\d{4})-(\d{2})-(\d{2})/
const ISO_DATE_PATTERN = /^\d{4}-\d{2}-\d{2}$/

const toIsoDate = ({ year, month, day }) =>
  `${year.toString().padStart(4, '0')}-${month.toString().padStart(2, '0')}-${day.toString().padStart(2, '0')}`

const parseDateParts = value => {
  const match = String(value ?? '').match(DATE_PREFIX_PATTERN)
  if (!match) {
    return null
  }

  const parts = {
    year: Number(match[1]),
    month: Number(match[2]),
    day: Number(match[3]),
  }
  const date = new Date(Date.UTC(parts.year, parts.month - 1, parts.day))

  if (
    date.getUTCFullYear() !== parts.year ||
    date.getUTCMonth() !== parts.month - 1 ||
    date.getUTCDate() !== parts.day
  ) {
    return null
  }

  return parts
}

const formatDateForInput = dateString => {
  if (!dateString) return ''
  const dateParts = parseDateParts(dateString)
  if (dateParts) return toIsoDate(dateParts)

  const date = new Date(dateString)
  if (isNaN(date)) return ''

  return toIsoDate({
    year: date.getFullYear(),
    month: date.getMonth() + 1,
    day: date.getDate(),
  })
}

const isValidDateInput = value => {
  if (!ISO_DATE_PATTERN.test(value)) {
    return false
  }

  const dateParts = parseDateParts(value)
  return Boolean(dateParts) && toIsoDate(dateParts) === value
}

const syncDisplayValue = value => {
  const formattedValue = formatDateForInput(value)
  displayValue.value = formattedValue
  lastCommittedValue.value = formattedValue
}

const update = () => {
  if (!displayValue.value) {
    if (props.allowEmpty) {
      lastCommittedValue.value = ''
      emits('update', null)
    }

    return
  }

  if (!isValidDateInput(displayValue.value)) {
    return
  }

  lastCommittedValue.value = displayValue.value
  emits('update', displayValue.value)
}

const stopEditing = () => {
  isEditing.value = false
  update()
}


watch(
  () => props.data,
  newValue => {
    if (isEditing.value) {
      return
    }
    syncDisplayValue(newValue)
  },
  { immediate: true },
)

const computedDisplayValue = computed(() => {
  // Assuming you still want to display the date in a different format
  const options = { year: 'numeric', month: 'long', day: 'numeric' }
  const formattedDate = formatDateForInput(props.data)

  if (!formattedDate) {
    return ''
  }

  const [year, month, day] = formattedDate.split('-').map(Number)
  return new Intl.DateTimeFormat('en-GB', options).format(
    new Date(year, month - 1, day),
  )
})
</script>

<template>
  <input
    v-if="editable"
    type="date"
    v-model="displayValue"
    :disabled="!props.editable"
    :class="props.classes"
    @focus="isEditing = true"
    @input="update"
    @blur="stopEditing"
  />
  <span v-else>
    <template v-if="props.data">{{ computedDisplayValue }}</template></span>
</template>
